import { useAuth } from "@Auth/useAuth";
import { useDrawer } from "@hooks/useDrawer";
import { useTagsStore } from "@stores/tagsStore";
import { useEffect, useReducer } from "react";
import { Section } from "@common/SimpleDrawer/components/Section";
import { FormInput } from "@components/FormInput/FormInput";
import { Footer } from "@common/SimpleDrawer/components/Footer";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { onSuccessShow } from "@utils/onSuccessShow";
import { onErrorShow } from "@utils/onErrorShow";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { addTransparency } from "@utils/colorUtils";
import { DashBox } from "@common/DashBox";
import { DeleteOutline } from "@mui/icons-material";
import { onInfoShow } from "@utils/onInfoShow";

type TagInitialState = {
  name: string;
  color: string;
  errors: Record<keyof TagInitialState, string>;
  tagValues: { value: string; color: string; id: string }[];
  tagValuesErrors: { id: string; error: string; value: string }[];
};

const initialState: TagInitialState = {
  name: "",
  color: "",
  errors: {} as Record<keyof TagInitialState, string>,
  tagValues: [],
  tagValuesErrors: [],
};

enum TAG_ACTIONS {
  SET_FIELD = "SET_FIELD",
  ADD_TAG_VALUE = "ADD_TAG_VALUE",
  REMOVE_TAG_VALUE = "REMOVE_TAG_VALUE",
  SET_ERRORS = "SET_ERRORS",
  TAG_VALUE_SET_FIELD = "TAG_VALUE_SET_FIELD",
  TAG_VALUES_SET_ERRORS = "TAG_VALUES_SET_ERRORS",
  CLEAR_ERRORS = "CLEAR_ERRORS",
  SET_TAG_DEFINITION = "SET_TAG_DEFINITION",
  SET_TAG_VALUES = "SET_TAG_VALUES",
}

type TagValueError = { id: string; error: string; value: string };

type BaseAction = {
  type: TAG_ACTIONS;
  field?: keyof TagInitialState;
  value?: string | null;
  id?: string;
  tagField?: "value" | "color";
};

type SetErrorsAction = BaseAction & {
  type: TAG_ACTIONS.SET_ERRORS;
  errors: Record<keyof TagInitialState, string>;
};

type SetTagValuesErrorsAction = BaseAction & {
  type: TAG_ACTIONS.TAG_VALUES_SET_ERRORS;
  errors: TagValueError[];
};

type SetTagValuesAction = BaseAction & {
  type: TAG_ACTIONS.SET_TAG_VALUES;
  values: { value: string; color: string; id: string }[];
};

type Action =
  | BaseAction
  | SetErrorsAction
  | SetTagValuesErrorsAction
  | SetTagValuesAction;

function formReducer(state: TagInitialState, action: Action): TagInitialState {
  switch (action.type) {
    case TAG_ACTIONS.SET_FIELD: {
      if (!action.field) return state;
      return {
        ...state,
        [action.field!]: action.value,
      };
    }
    case TAG_ACTIONS.SET_ERRORS:
      if ("errors" in action) {
        return {
          ...state,
          errors: action.errors,
        };
      }
      return state;
    case TAG_ACTIONS.ADD_TAG_VALUE:
      return {
        ...state,
        tagValues: [
          ...state.tagValues,
          { value: "", color: "", id: crypto.randomUUID() },
        ],
      };
    case TAG_ACTIONS.REMOVE_TAG_VALUE:
      return {
        ...state,
        tagValues: state.tagValues.filter(
          (tagValue) => tagValue.id !== action.id
        ),
      };
    case TAG_ACTIONS.TAG_VALUE_SET_FIELD:
      return {
        ...state,
        tagValues: state.tagValues.map((tagValue) =>
          tagValue.id === action.id
            ? { ...tagValue, [action.tagField!]: action.value }
            : tagValue
        ),
      };
    case TAG_ACTIONS.TAG_VALUES_SET_ERRORS:
      if ("errors" in action) {
        return {
          ...state,
          tagValuesErrors: action.errors,
        };
      }
      return state;
    case TAG_ACTIONS.CLEAR_ERRORS:
      return {
        ...state,
        errors: {} as Record<keyof TagInitialState, string>,
        tagValuesErrors: [],
      };

    case TAG_ACTIONS.SET_TAG_VALUES:
      if ("values" in action) {
        return {
          ...state,
          tagValues: action.values,
        };
      }
      return state;
    default:
      return state;
  }
}

type TagFormProps = {
  handleClose: () => void;
};

export const TagForm = ({ handleClose }: TagFormProps) => {
  const {
    context,
    selectedId,
    close: closeOnSubmit,
  } = useDrawer();

  const { setTouch } = useConfirmationModalStore();

  const { baseUser } = useAuth();
  const {
    tagDefinitions,
    createTagDefinition,
    createTagValue,
    updateTagDefinition,
    updateTagValue,
    deleteTagValue,
    // loading,
    tagValues,
  } = useTagsStore();

  useEffect(() => {
    if (selectedId && context === DRAWER_CONTEXT.EDIT_TAG) {
      const tag = tagDefinitions.find((tag) => tag.id === selectedId);
      const values = tagValues.filter(
        (tagValue) => tagValue.tag_definition_id === selectedId
      );
      if (tag) {
        dispatch({
          type: TAG_ACTIONS.SET_FIELD,
          field: "name",
          value: tag.key,
        });
      }
      if (values.length > 0) {
        dispatch({
          type: TAG_ACTIONS.SET_TAG_VALUES,
          values: values.map((value) => ({
            value: value.value,
            color: value.metadata.color as string,
            id: value.id,
          })),
        });
      }
    }
  }, [selectedId, context, tagDefinitions, tagValues]);

  const [state, dispatch] = useReducer(formReducer, initialState);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: TAG_ACTIONS.SET_FIELD,
      field: e.target.name as keyof TagInitialState,
      value: e.target.value,
    });
    setTouch(true);
  };

  const handleFormVerification = () => {
    const errors = {} as Record<keyof TagInitialState, string>;

    const requiredFields: (keyof TagInitialState)[] = ["name"];

    requiredFields.forEach((field) => {
      if (!state[field]) errors[field] = `${field} is required`;
    });

    dispatch({
      type: TAG_ACTIONS.SET_ERRORS,
      errors,
    });
    if (Object.values(errors).some((error) => error !== "")) {
      setTouch(true);
      return;
    }
    return true;
  };

  const handleCreate = async () => {
    try {
      dispatch({
        type: TAG_ACTIONS.CLEAR_ERRORS,
      });
      if (!handleFormVerification()) return;

      const tagValuesErrors: { id: string; error: string; value: string }[] =
        state.tagValues.map((tagValue) => {
          if (!tagValue.value)
            return {
              error: "Value is required",
              id: tagValue.id,
              value: tagValue.value,
            };
          if (!tagValue.color)
            return {
              error: "Color is required",
              id: tagValue.id,
              value: tagValue.value,
            };
          return { error: "", value: "", id: tagValue.id };
        });

      console.log(tagValuesErrors);

      if (tagValuesErrors.some((values) => values.error !== "")) {
        dispatch({
          type: TAG_ACTIONS.TAG_VALUES_SET_ERRORS,
          errors: tagValuesErrors,
        });
        return;
      }

      if (context === DRAWER_CONTEXT.EDIT_TAG && selectedId) {
        if (
          state.name !== tagDefinitions.find((t) => t.id === selectedId)?.key
        ) {
          await updateTagDefinition({
            id: selectedId,
            key: state.name,
            organization_id: baseUser!.organization_id!,
          });
        }

        state.tagValues
          .filter((tag) => {
            const tagValue = tagValues.find((t) => t.id === tag.id);
            if (tagValue) {
              return tagValue.value !== tag.value;
            }
            return false;
          })
          .forEach(async (tagValue) => {
            await updateTagValue({
              id: tagValue.id,
              value: tagValue.value,
              metadata: { color: tagValue.color },
              organization_id: baseUser!.organization_id!,
              tag_definition_id: selectedId,
            });
          });
        state.tagValues
          .filter((tag) => {
            return !tagValues.map((t) => t.id).includes(tag.id);
          })
          .forEach(async (tagValue) => {
            await createTagValue({
              tag_definition_id: selectedId,
              value: tagValue.value,
              metadata: { color: tagValue.color },
              organization_id: baseUser!.organization_id!,
            });
          });
      } else {
        const createdTagDefinition = await createTagDefinition({
          key: state.name,
          organization_id: baseUser!.organization_id!,
        });

        state.tagValues.forEach(async (tagValue) => {
          await createTagValue({
            tag_definition_id: createdTagDefinition.id,
            value: tagValue.value,
            metadata: {
              color: tagValue.color,
            },
            organization_id: baseUser!.organization_id!,
          });
        });
      }
      onSuccessShow("Tag created successfully");
      closeOnSubmit();
      setTouch(false);
    } catch (error) {
      onErrorShow("Error creating tag");
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto", pb: 2 }}>
        <Section>
          <FormInput
            name="name"
            label="Name"
            value={state.name}
            onChange={handleChange}
            errorMessage={state.errors.name}
          />
        </Section>
        <Section>
          <Typography variant="h5">Tag Values</Typography>
          <div
            style={{
              display: "flex",
              width: "100%",
              marginBottom: "16px",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {state.tagValues.map((tagValue) => {
              return (
                <div
                  key={tagValue.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    gap: "8px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <FormInput
                      name="tagValue"
                      value={tagValue.value}
                      style={{ width: "100%" }}
                      onChange={(e) => {
                        dispatch({
                          type: TAG_ACTIONS.TAG_VALUE_SET_FIELD,
                          id: tagValue.id,
                          tagField: "value",
                          value: e.target.value,
                        });
                      }}
                    />
                  </div>
                  <div style={{ width: "80px" }}>
                    <FormInput
                      name="color"
                      style={{ width: "100%" }}
                      value={tagValue.color}
                      onChange={(e) => {
                        dispatch({
                          type: TAG_ACTIONS.TAG_VALUE_SET_FIELD,
                          id: tagValue.id,
                          tagField: "color",
                          value: e.target.value,
                        });
                      }}
                      type="color"
                      errorMessage={state.errors.color}
                    />
                  </div>
                  <Chip
                    label={tagValue.value}
                    sx={{
                      backgroundColor: addTransparency(tagValue.color, 0.1),
                      height: "28px",
                      fontSize: "14px",
                      width: "fit-content",
                      padding: " 2px 8px",
                      border: `1px solid ${tagValue.color}`,
                      color: tagValue.color,
                    }}
                  />
                  <IconButton
                    onClick={async () => {
                      if (context === DRAWER_CONTEXT.EDIT_TAG) {
                        const existingTagValue = tagValues.find(
                          (t) => t.id === tagValue.id
                        );
                        if (existingTagValue) {
                          await deleteTagValue(existingTagValue.id);
                          dispatch({
                            type: TAG_ACTIONS.REMOVE_TAG_VALUE,
                            id: tagValue.id,
                          });
                          onInfoShow("Tag value deleted successfully");
                        }
                        return;
                      }
                      dispatch({
                        type: TAG_ACTIONS.REMOVE_TAG_VALUE,
                        id: tagValue.id,
                      });
                    }}
                    color="error"
                    style={{ flexShrink: 0 }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </div>
              );
            })}
            {state.tagValuesErrors.length > 0 && (
              <Typography variant="caption" color="error">
                {state.tagValuesErrors.map((error) => error.error).join(", ")}
              </Typography>
            )}
          </div>
          <DashBox>
            <Button
              disabled={
                state.tagValues.length > 0 &&
                state.tagValues.some((tag) => tag.value === "")
              }
              onClick={() => dispatch({ type: TAG_ACTIONS.ADD_TAG_VALUE })}
              size="small"
              variant="outlined"
            >
              Add Tag Value
            </Button>
          </DashBox>
        </Section>
      </Box>

      <Footer
        handleClose={handleClose}
        handleSubmit={async (e) => {
          e.preventDefault();
          await handleCreate();
        }}
      />
    </Box>
  );
};
